import {
  DeleteOutlined,
  PlusCircleOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  Button,
  Input,
  notification,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
} from 'antd';
import { React, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useGetMpAttributes } from '../../../../../../utils/api/mpAttributes/useGetMpAttributes';
import { useDeleteShoppingFeedAttribute } from '../../../../../../utils/api/mpCategories/useDeleteShoppingFeedAttribute';
import AddAttributesDrawer from '../../../attributes/AddAttributesDrawer';
import CreateAttributeDrawer from '../../../attributes/CreateAttributeDrawer';

// At or below this many values, every value is rendered inline as a tag.
// Above it, only a search box is shown and tags appear as the user types
// (and the number of rendered matches is capped to this same value).
const INLINE_VALUES_THRESHOLD = 100;

const AttributeValuesPanel = ({ values }) => {
  const [valueSearch, setValueSearch] = useState('');
  const isLargeList = values.length > INLINE_VALUES_THRESHOLD;
  const query = valueSearch.toLowerCase().trim();

  const filteredValues = useMemo(() => {
    if (!query) return values;
    return values.filter(
      (value) =>
        String(value.label ?? '')
          .toLowerCase()
          .includes(query) ||
        String(value.code ?? '')
          .toLowerCase()
          .includes(query)
    );
  }, [values, query]);

  // For large lists, only render tags once the user has typed something.
  const shouldRenderTags = !isLargeList || query.length > 0;
  const displayedValues = filteredValues.slice(0, INLINE_VALUES_THRESHOLD);
  const hiddenCount = filteredValues.length - displayedValues.length;

  return (
    <div>
      <div style={{ marginBottom: 8, fontWeight: 500 }}>
        Valeurs (
        {query ? `${filteredValues.length} / ${values.length}` : values.length})
      </div>
      {isLargeList && (
        <Input
          size="small"
          placeholder="Rechercher une valeur par libellé ou code..."
          prefix={<SearchOutlined />}
          value={valueSearch}
          onChange={(e) => setValueSearch(e.target.value)}
          allowClear
          style={{ maxWidth: 320, marginBottom: 8 }}
        />
      )}
      {shouldRenderTags ? (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            alignItems: 'center',
          }}
        >
          {displayedValues.map((value, index) => (
            <Tag key={`${value.code}-${index}`}>
              {value.label}
              {value.code != null && value.code !== value.label
                ? ` (${value.code})`
                : ''}
            </Tag>
          ))}
          {displayedValues.length === 0 && (
            <span style={{ color: '#bababa' }}>
              Aucune valeur ne correspond
            </span>
          )}
          {hiddenCount > 0 && (
            <span style={{ color: '#999' }}>
              … et {hiddenCount} autre(s). Affinez votre recherche.
            </span>
          )}
        </div>
      ) : (
        <span style={{ color: '#999' }}>
          Saisissez un terme pour rechercher parmi {values.length} valeurs.
        </span>
      )}
    </div>
  );
};

const Attributes = ({ category }) => {
  const { marketplace } = useParams();
  const isShoppingFeed = marketplace === 'shoppingfeed';

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState(null);
  const [exigenceFilter, setExigenceFilter] = useState(null);
  const [isAddDrawerOpen, setIsAddDrawerOpen] = useState(false);
  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(false);

  const { deleteAttribute, isDeleting } = useDeleteShoppingFeedAttribute();

  const { mpAttributes, isLoading } = useGetMpAttributes(
    {
      categories__mp_category__id: category?.id,
      categories__selected: true,
    },
    { enabled: !!category?.id }
  );

  const attributes = useMemo(() => {
    if (!mpAttributes || mpAttributes.length === 0) return [];

    return mpAttributes.map((attribute) => {
      const type = attribute.format?.toLowerCase() || 'text';

      const requirement =
        attribute.metadata?.requirement || attribute.requirement || 'optional';
      const required = requirement === 'required';
      const recommended = requirement === 'recommended';

      return {
        key: attribute.id.toString(),
        id: attribute.id,
        name: attribute.name,
        code: attribute.code,
        type: type,
        required: required,
        recommended: recommended,
        values: attribute.values_list || attribute.metadata?.values || [],
        createdAt: attribute.created_at,
        updatedAt: attribute.updated_at,
      };
    });
  }, [mpAttributes]);

  const getStatusColor = (record) => {
    if (record.required) return '#ff4d4f';
    if (record.recommended) return '#fa8c16';
    return '#52c41a';
  };

  const getExigenceLevel = (record) => {
    if (record.required) return 'requis';
    if (record.recommended) return 'conseillé';
    return 'optionnel';
  };

  const handleDeleteAttribute = (attributeId) => {
    deleteAttribute(
      { categoryId: category?.id, attributeId },
      {
        onSuccess: () => {
          notification.success({
            message: 'Attribut supprimé',
            description: "L'attribut a été supprimé avec succès",
            placement: 'bottomRight',
            duration: 3,
          });
        },
        onError: (error) => {
          notification.error({
            message: 'Erreur',
            description:
              error?.response?.data?.error ||
              error?.message ||
              "Impossible de supprimer l'attribut",
            placement: 'bottomRight',
            duration: 3,
          });
        },
      }
    );
  };

  const filteredAttributes = useMemo(() => {
    return attributes.filter((attribute) => {
      const matchSearch =
        !search ||
        attribute.name.toLowerCase().includes(search.toLowerCase()) ||
        attribute.code.toLowerCase().includes(search.toLowerCase());
      const matchType = !typeFilter || attribute.type === typeFilter;

      const matchExigence =
        !exigenceFilter || getExigenceLevel(attribute) === exigenceFilter;

      return matchSearch && matchType && matchExigence;
    });
  }, [search, typeFilter, exigenceFilter, attributes]);

  const columns = [
    {
      title: "Nom de l'attribut",
      dataIndex: 'name',
      key: 'name',
      width: 200,
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span
            style={{
              display: 'inline-block',
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: getStatusColor(record),
              flexShrink: 0,
            }}
          />
          <span>{text}</span>
        </div>
      ),
    },
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      width: 150,
      sorter: (a, b) => a.code.localeCompare(b.code),
      render: (text) => <code style={{ fontSize: '12px' }}>{text}</code>,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 100,
      sorter: (a, b) => a.type.localeCompare(b.type),
      render: (text) => {
        const colors = {
          text: 'blue',
          number: 'green',
          list: 'purple',
        };
        return <Tag color={colors[text] || 'default'}>{text}</Tag>;
      },
    },
    ...(isShoppingFeed
      ? [
          {
            title: 'Actions',
            key: 'actions',
            width: 80,
            render: (_, record) => (
              <Popconfirm
                title="Supprimer cet attribut ?"
                description="Cette action est irréversible."
                onConfirm={() => handleDeleteAttribute(record.id)}
                okText="Supprimer"
                cancelText="Annuler"
                okButtonProps={{ danger: true, loading: isDeleting }}
              >
                <Button
                  type="text"
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                />
              </Popconfirm>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          padding: '0',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '16px',
          }}
        >
          <Input
            size="small"
            placeholder="Rechercher un attribut par nom ou code..."
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            allowClear
            style={{ flex: 1, maxWidth: '500px' }}
          />
          <div
            style={{
              display: 'flex',
              gap: '16px',
            }}
          >
            <Button
              size="small"
              type="primary"
              icon={<PlusCircleOutlined />}
              onClick={() => setIsAddDrawerOpen(true)}
            >
              Ajouter un attribut
            </Button>
            {isShoppingFeed && (
              <Button
                size="small"
                type="primary"
                icon={<PlusCircleOutlined />}
                onClick={() => setIsCreateDrawerOpen(true)}
              >
                Créer un attribut
              </Button>
            )}
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            margin: '8px 0',
            gap: '16px',
          }}
        >
          <Space size="middle" style={{ flex: 1 }}>
            <Select
              placeholder="Type"
              size="small"
              style={{ width: 150 }}
              allowClear
              showSearch
              value={typeFilter}
              onChange={setTypeFilter}
              options={[
                { label: 'Texte', value: 'text' },
                { label: 'Nombre entier', value: 'number' },
                { label: 'Décimal', value: 'decimal' },
                { label: 'Liste', value: 'list' },
                { label: 'Booléen', value: 'boolean' },
              ]}
            />
            <Select
              placeholder="Niveau d'exigence"
              style={{ width: 180 }}
              allowClear
              size="small"
              showSearch
              value={exigenceFilter}
              onChange={setExigenceFilter}
              options={[
                {
                  label: (
                    <span>
                      <span
                        style={{
                          display: 'inline-block',
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: '#ff4d4f',
                          marginRight: '8px',
                        }}
                      />
                      Requis
                    </span>
                  ),
                  value: 'requis',
                },
                {
                  label: (
                    <span>
                      <span
                        style={{
                          display: 'inline-block',
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: '#fa8c16',
                          marginRight: '8px',
                        }}
                      />
                      Conseillé
                    </span>
                  ),
                  value: 'conseillé',
                },
                {
                  label: (
                    <span>
                      <span
                        style={{
                          display: 'inline-block',
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: '#52c41a',
                          marginRight: '8px',
                        }}
                      />
                      Optionnel
                    </span>
                  ),
                  value: 'optionnel',
                },
              ]}
            />
          </Space>
          <Tag color="blue" style={{ fontSize: '14px' }}>
            {filteredAttributes.length} attribut
            {filteredAttributes.length > 1 ? 's' : ''}
          </Tag>
        </div>

        <div style={{ flex: 1, overflow: 'hidden' }}>
          <Table
            columns={columns}
            dataSource={filteredAttributes}
            loading={isLoading}
            size="small"
            bordered
            scroll={{ x: 'max-content', y: 'calc(100vh - 400px)' }}
            pagination={false}
            expandable={{
              rowExpandable: (record) =>
                record.type === 'list' && record.values.length > 0,
              expandedRowRender: (record) => (
                <AttributeValuesPanel values={record.values} />
              ),
            }}
          />
        </div>
      </div>

      <AddAttributesDrawer
        open={isAddDrawerOpen}
        category={category}
        mpAttributes={mpAttributes}
        onClose={() => setIsAddDrawerOpen(false)}
      />

      <CreateAttributeDrawer
        open={isCreateDrawerOpen}
        category={category}
        onClose={() => setIsCreateDrawerOpen(false)}
      />
    </>
  );
};

export default Attributes;
